import { useGoogleLogin } from '@react-oauth/google';
import type { AppleAuthResponse, AppleUser } from 'react-apple-signin-auth';

import type { SocialType } from '@/shared/api/types';

import { useFacebookLogin } from '../lib/useFacebookLogin';

// Receives what the provider returned: an access token (an id token for Apple)
// and, on the very first Apple sign-in, the user's name and email
type OnSocialToken = (
  socialType: SocialType,
  accessToken: string,
  user?: AppleUser,
) => void;

// Starts the sign-in of each provider and hands its token to `onToken`.
// The pages decide what to do with it: sign in, sign up or link the account.
export const useSocialLogin = (onToken: OnSocialToken) => {
  const requestFacebookToken = useFacebookLogin(
    import.meta.env.VITE_FACEBOOK_APP_ID,
  );

  const requestGoogleToken = useGoogleLogin({
    onSuccess: (response) => {
      if (response?.access_token) onToken('google', response.access_token);
    },
  });

  const loginWithGoogle = () => requestGoogleToken();

  const loginWithFacebook = async () => {
    try {
      onToken('facebook', await requestFacebookToken());
    } catch (authError) {
      console.error('facebook auth error', authError);
    }
  };

  // For both `onSuccess` and `onError` of the Apple button
  const handleAppleResponse = (response: AppleAuthResponse) => {
    const idToken = response?.authorization?.id_token;

    if (idToken) onToken('apple', idToken, response.user);
  };

  return { loginWithGoogle, loginWithFacebook, handleAppleResponse };
};
